import { useState } from "react";
import { API } from "@/lib/apiClient";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Check, WindowsLogo, AppleLogo, LinuxLogo, ArrowsClockwise } from "@phosphor-icons/react";

function CmdBlock({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    // Trailing newline: pasting into a terminal runs it once, and a second accidental paste
    // starts on a fresh prompt instead of concatenating onto the same line.
    navigator.clipboard.writeText(value + "\n");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div>
      <div className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-1.5">{label}</div>
      <div className="flex items-stretch gap-2">
        <div className="flex-1 bg-black border border-zinc-800 p-3 rounded-md font-mono text-xs text-zinc-300 break-all overflow-x-auto">
          {value}
        </div>
        <button
          data-testid={testId}
          onClick={copy}
          className="shrink-0 w-11 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md transition-colors"
        >
          {copied ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} className="text-zinc-300" />}
        </button>
      </div>
    </div>
  );
}

export function UpdateAgentDialog({ open, onOpenChange, machine }) {
  if (!machine) return null;

  // Tiny, paste-proof one-liners: they fetch a hosted updater script from the server and run it.
  // The heavy lifting lives server-side (keeps the machine paired, stops only the Controlly agent).
  const winCmd = `irm ${API}/agent/update.ps1 | iex`;
  const nixCmd = `curl -fsSL ${API}/agent/update.sh | bash`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-white/10 sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="update-agent-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight flex items-center gap-2.5">
            <ArrowsClockwise size={20} className="text-amber-400" /> Update the agent on {machine.name}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            Run one of these <span className="text-zinc-200">on {machine.name}</span> — the easiest way is to paste it into a terminal while you're remote-controlling it. It swaps in the latest agent and restarts it. <span className="text-zinc-200">The machine stays paired</span> (no reinstall).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-2 text-xs bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-md px-3 py-2">
            <ArrowsClockwise size={14} /> Your live view will blink out for a second or two while it restarts, then reconnect on the new version.
          </div>

          <CmdBlock
            testId="update-cmd-windows"
            label={<><WindowsLogo size={12} className="inline mr-1" /> Windows — paste into PowerShell</>}
            value={winCmd}
          />
          <p className="text-xs text-zinc-500 leading-relaxed">
            On {machine.name}, open <span className="font-mono text-zinc-300">PowerShell</span> (Start menu → type "PowerShell"), <span className="text-zinc-300">paste once and press Enter</span>. It updates and restarts only the Controlly agent — nothing else.
          </p>

          <div className="pt-2 border-t border-white/10">
            <CmdBlock
              testId="update-cmd-unix"
              label={<><AppleLogo size={12} className="inline mr-0.5" /><LinuxLogo size={12} className="inline mr-1" /> Mac / Linux — run in the agent's folder</>}
              value={nixCmd}
            />
            <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
              <span className="text-zinc-300">First</span> <span className="font-mono text-zinc-300">cd</span> into the folder where you installed the agent (it holds <span className="font-mono text-zinc-300">device.key</span>), then run this.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button data-testid="close-update-dialog" onClick={() => onOpenChange(false)} className="bg-blue-600 hover:bg-blue-500">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
